/**
 * Admin → client → Secure links (ACTION_LINKS_PLAN.md section 7): the links staff sent this client from the
 * CRM, newest first, read-only, with Withdraw (a reason, kept in the admin audit) on a live one. The link
 * itself is never shown here; a custom link shows the address staff pasted.
 */
import { useState } from 'react';
import { fmtDateTime } from '@platform/shared';
import { cx, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useLoad } from '../stores/account';

const input = 'h-8 w-full rounded-lg border border-line bg-bg px-3 text-[12.5px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const btn = 'h-8 shrink-0 rounded-md border border-line-strong px-3 text-xs font-semibold text-fg disabled:opacity-50';
const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

interface LinkRow {
  linkId: string;
  type: string;
  kind: string | null;
  url: string | null;
  title: string;
  channels: string[];
  pushQueued: number;
  status: 'live' | 'completed' | 'expired' | 'revoked';
  agentName: string;
  channel: string;
  createdAt: string;
  expiresAt: string;
  openedAt: string | null;
  closedAt: string | null;
  closeReason: string | null;
}

function statusOf(l: LinkRow): { text: string; tone: string } {
  if (l.status === 'live') return { text: l.openedAt ? 'Live · opened' : 'Live', tone: 'text-accent' };
  if (l.status === 'completed') return { text: l.type === 'custom' ? 'Opened' : 'Done', tone: 'text-up' };
  if (l.status === 'expired') return { text: 'Expired', tone: 'text-muted' };
  return { text: 'Withdrawn', tone: 'text-muted' };
}

const hostOf = (url: string | null) => {
  try {
    return url ? new URL(url).hostname : '';
  } catch {
    return '';
  }
};

export function ActionLinksCard({ userId }: { userId: string }) {
  const { data, reload, error } = useLoad<{ data: LinkRow[] }>(`/api/admin/users/${userId}/action-links`);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  if (error) return <p className="m-0 text-xs text-down">{error}</p>;
  if (!data) return <Spinner />;
  const withdraw = async (id: string) => {
    if (!reason.trim()) {
      setMsg({ ok: false, text: 'Give a reason' });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await api.post(`/api/admin/users/${userId}/action-links/${id}/withdraw`, { reason: reason.trim() });
      setMsg({ ok: true, text: "Withdrawn: removed from the client's app, bell and phone" });
      setRevoking(null);
      setReason('');
      await reload();
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="m-0 text-sm font-semibold">Secure links</h3>
        <span className="text-[11px] text-muted">Sent from the CRM · removed from the client's app once done</span>
      </div>
      {!data.data.length && <p className="m-0 text-xs text-muted">No secure links sent to this client.</p>}
      {data.data.map((l) => {
        const st = statusOf(l);
        const host = hostOf(l.url);
        return (
          <div key={l.linkId} className="flex flex-col gap-2 border-t border-line pt-2 first-of-type:border-t-0 first-of-type:pt-0">
            <div className="flex items-start justify-between gap-3 text-xs">
              <span className="min-w-0">
                <span className="block font-semibold">
                  {l.title}
                  {host && (
                    <span className="font-normal text-muted" title={l.url ?? undefined}>
                      {' '}
                      · {host}
                    </span>
                  )}
                </span>
                <span className="text-[11px] text-muted">
                  {l.agentName} · {fmtDateTime(l.createdAt)} · {l.channels.includes('push') ? (l.pushQueued ? `app + push to ${l.pushQueued}` : 'app, no device') : 'app only'}
                  {l.status === 'live' ? ` · until ${fmtDateTime(l.expiresAt)}` : l.closedAt ? ` · ${fmtDateTime(l.closedAt)}` : ''}
                  {l.status === 'revoked' && l.closeReason ? ` · ${l.closeReason}` : ''}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <span className={cx('text-[11px] font-semibold', st.tone)}>{st.text}</span>
                {l.status === 'live' && revoking !== l.linkId && (
                  <button
                    type="button"
                    className={btn}
                    onClick={() => {
                      setRevoking(l.linkId);
                      setReason('');
                      setMsg(null);
                    }}
                  >
                    Withdraw
                  </button>
                )}
              </span>
            </div>
            {revoking === l.linkId && (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  id={`al-reason-${l.linkId}`}
                  className={cx(input, 'min-w-0 flex-1')}
                  placeholder="Why (kept in the audit log)"
                  value={reason}
                  maxLength={500}
                  onChange={(e) => setReason(e.target.value)}
                />
                <button type="button" className={btn} onClick={() => setRevoking(null)}>
                  Cancel
                </button>
                <button type="button" className={cx(btn, 'border-down text-down')} disabled={busy} onClick={() => void withdraw(l.linkId)}>
                  {busy ? 'Withdrawing…' : 'Withdraw link'}
                </button>
              </div>
            )}
          </div>
        );
      })}
      {msg && <span className={cx('text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</span>}
    </div>
  );
}
